import {
  Block,
  IconNames,
  SvgIcon,
  Touchable,
  Typography,
  TypographyVariants,
  palette,
  spacing,
  useBreakpoints,
} from "@stepapp/ui";
import React from "react";
import { useTranslation } from "react-i18next";
import { StyleProp, StyleSheet, ViewStyle } from "react-native";
import { useAppLinks } from "../hooks";
import { AppStorage } from "../services/AppStorage";
import { useDisplayIntercom } from "../services/Intercom";

type Props = {
  type: "oneTime" | "subscription" | "date" | "payment" | "review";
  containerStyles?: StyleProp<ViewStyle>;
};

type ServiceNoticeItem = {
  styles: StyleProp<ViewStyle>;
  svgColor: string;
  icon: IconNames;
  title: string;
  titleVariant: TypographyVariants;
  description?: string;
  descriptionVariant?: TypographyVariants;
};

const ServiceNotice = ({ type, containerStyles }: Props) => {
  const { t } = useTranslation();
  const { sm } = useBreakpoints();
  const countryCode = AppStorage().get("countryCode");
  const displayIntercom = useDisplayIntercom(countryCode);
  const { TermsOfUse } = useAppLinks();

  const serviceData = {
    oneTime: {
      styles: {
        backgroundColor: palette.primary.lightTurquoise,
        alignItems: "center",
      },
      svgColor: palette.primary.turquoise,
      icon: "offers-filled",
      title: t("screens.service.serviceDateInfo", { hours: 48 }),
      titleVariant: sm ? "paragraph" : "lightLabel",
    },
    subscription: {
      styles: {
        backgroundColor: palette.primary.lightPurple,
        alignItems: "center",
      },
      svgColor: palette.primary.purple,
      icon: "offers-filled",
      title: t("screens.service.serviceDateInfo", { hours: 36 }),
      titleVariant: sm ? "paragraph" : "lightLabel",
    },
    date: {
      styles: {
        backgroundColor: palette.secondary.green,
        alignItems: "center",
      },
      svgColor: palette.tertiary.green,
      icon: "questionmark-outline",
      title: t("screens.calendar.cantFindDate"),
      description: t("screens.calendar.writeToUs"),
      titleVariant: sm ? "paragraph" : "lightLabel",
      descriptionVariant: sm ? "paragraphBold" : "lightLabelBold",
    },
    payment: {
      styles: {
        backgroundColor: palette.secondary.lightGreen,
        alignItems: "center",
      },
      svgColor: palette.primary.green,
      icon: "verified",
      title: t("screens.serviceSummary.yourDataIsSecureThanksToStripe"),
      titleVariant: sm ? "newBodyBold" : "label",
      description: t("screens.serviceSummary.paymentTerms", {
        link: TermsOfUse,
      }),
      descriptionVariant: sm ? "paragraph" : "lightLabel",
    },
    review: {
      styles: {
        backgroundColor: palette.secondary.lightGreen,
      },
      svgColor: palette.primary.green,
      icon: "shield-star",
      title: t("screens.reviews.onlyVerifiedReviews"),
      titleVariant: sm ? "newBodyBold" : "label",
      description: t("screens.reviews.trueReviews"),
      descriptionVariant: sm ? "paragraph" : "lightLabel",
    },
  };

  const isDateInfo = type === "date";
  const isReview = type === "review";

  const data = serviceData?.[type] as ServiceNoticeItem;

  return (
    <Block
      row
      marginBottom={3}
      paddingHorizontal={sm ? 3 : 2}
      style={[styles.container, containerStyles, data.styles]}
    >
      <SvgIcon
        color={data.svgColor}
        name={data.icon}
        size={24}
        style={isReview ? styles.reviewIcon : styles.icon}
      />
      <Typography style={{ marginLeft: spacing.unit * (sm ? 3 : 2) }}>
        {isDateInfo ? (
          <Touchable onPress={() => displayIntercom()}>
            <Typography variant={data.titleVariant}>
              {data.title}{" "}
              <Typography decoration="underline" variant={data?.descriptionVariant}>
                {data?.description}
              </Typography>
            </Typography>
          </Touchable>
        ) : (
          <Block>
            <Typography variant={data.titleVariant}>{data.title}</Typography>
            {data?.description && (
              <Typography variant={data?.descriptionVariant}>{data?.description}</Typography>
            )}
          </Block>
        )}
      </Typography>
    </Block>
  );
};
export default React.memo(ServiceNotice);

const styles = StyleSheet.create({
  container: {
    paddingTop: spacing.unit,
    paddingBottom: spacing.unit * 2,
    borderRadius: spacing.radius * 0.5,
  },
  icon: {
    minWidth: spacing.unit * 4,
  },
  reviewIcon: {
    minWidth: spacing.unit * 4,
    marginTop: spacing.unit,
  },
});
